import React from 'react';
import { Box, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { CsvDelimiter, CSV_DELIMITER_LABELS } from '../../utils/csvDelimiter';

interface DelimiterSelectorProps {
  delimiter: CsvDelimiter;
  onChange: (delimiter: CsvDelimiter) => void;
  disabled?: boolean;
}

/**
 * Shows which delimiter a CSV was parsed with and lets the user correct it.
 * Detection reads the header row, which is right for practically every export, but a
 * headerless file is genuinely ambiguous — this is the way out of that.
 */
const DelimiterSelector: React.FC<DelimiterSelectorProps> = ({
  delimiter,
  onChange,
  disabled = false,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="body2" color="text.secondary">
      CSV delimiter
    </Typography>
    <Select
      size="small"
      value={delimiter}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value as CsvDelimiter;
        if (next !== delimiter) onChange(next);
      }}
      sx={{ minWidth: 160, '& .MuiSelect-select': { py: 0.5 } }}
    >
      {(Object.keys(CSV_DELIMITER_LABELS) as CsvDelimiter[]).map((value) => (
        <MenuItem key={value} value={value}>
          {CSV_DELIMITER_LABELS[value]}
        </MenuItem>
      ))}
    </Select>
    <Tooltip title="Detected from the header row. Change it if the preview columns look wrong — European exports usually use a semicolon.">
      <HelpOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />
    </Tooltip>
  </Box>
);

export default DelimiterSelector;
